import type { TFunction } from 'i18next'

export const BUDGET_PERIOD_TYPES = ['onetime', 'weekly', 'monthly', 'custom'] as const
export type TBudgetPeriodType = (typeof BUDGET_PERIOD_TYPES)[number]

export const BUDGET_QUICK_AMOUNTS = [10, 25, 50, 100] as const
export const BUDGET_QUICK_AMOUNTS_SHORT = [10, 25, 50] as const

/** Radio card options for budget period selection — shared across all budget UIs */
export const BUDGET_PERIOD_RADIO_OPTIONS: readonly {
  value: TBudgetPeriodType
  titleKey: string
  subtitleKey: string
}[] = [
  { value: 'monthly', titleKey: 'Monthly', subtitleKey: 'Recharged every month' },
  { value: 'custom', titleKey: 'Period', subtitleKey: 'Recharged per period' },
  { value: 'onetime', titleKey: 'One-time', subtitleKey: 'One-time balance' },
]

/** Period choices when a card already has a recurring budget — one-time is not offered. */
export const BUDGET_PERIOD_RADIO_OPTIONS_RECURRING = BUDGET_PERIOD_RADIO_OPTIONS.filter(
  (option) => option.value !== 'onetime',
)

export const BUDGET_AMOUNT_MIN_CENTS = 1
export const BUDGET_AMOUNT_MAX_CENTS = 100000

export function eurosToCents(amountInEuros: number): number {
  return Math.round(amountInEuros * 100)
}

/** Inverse of eurosToCents: integer cents to euros. */
export function centsToEuros(amountInCents: number): number {
  return amountInCents / 100
}

export function isBudgetAmountInRange(amountInEuros: number): boolean {
  const cents = eurosToCents(amountInEuros)

  return cents >= BUDGET_AMOUNT_MIN_CENTS && cents <= BUDGET_AMOUNT_MAX_CENTS
}

export function isRecurringBudgetPeriodType(periodType: string | null | undefined): boolean {
  return periodType === 'weekly' || periodType === 'monthly' || periodType === 'custom'
}

export function toFormBudgetPeriodType(value: string | null | undefined): TBudgetPeriodType {
  if (value === 'onetime' || value === 'weekly' || value === 'monthly' || value === 'custom') return value

  return 'monthly'
}

export type TFrequencyOption = 'daily' | '3x_week' | '2x_week' | '1x_week' | 'custom'

export const FREQUENCY_TRIPS_MAP: Record<Exclude<TFrequencyOption, 'custom'>, number> = {
  daily: 22,
  '3x_week': 13,
  '2x_week': 9,
  '1x_week': 4,
}

export const RETURN_TRIP_FACTOR = 2
export const BUFFER_PERCENTAGE = 0.15

/**
 * Returns the translated label for a budget period type.
 * Uses static t() calls so i18next-parser can detect and extract the keys.
 */
export function getBudgetPeriodTypeLabel(periodType: string, t: TFunction): string {
  switch (periodType) {
    case 'onetime':
      return t('One-time')
    case 'weekly':
      return t('Weekly')
    case 'monthly':
      return t('Monthly')
    case 'custom':
      return t('Custom')
    default:
      return periodType
  }
}
